import { useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Settings2, Trash2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";

const TYPES = ["text", "longtext", "number", "date", "yesno", "select"];

export function useCustomFields(entity) {
  const [defs, setDefs] = useState([]);
  const reload = () => client.get(`/custom-fields/${entity}`).then((r) => setDefs(r.data)).catch(() => setDefs([]));
  useEffect(() => { reload(); }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps
  return { defs, reload };
}

export function CustomFieldsView({ defs, values }) {
  const { t } = useI18n();
  if (!defs?.length) return null;
  return (
    <div className="space-y-1.5 border-t border-slate-100 pt-2" data-testid="custom-fields-view">
      {defs.map((d) => {
        let v = (values || {})[d.key];
        if (d.type === "yesno") v = v ? t("common.yes", "Yes") : t("common.no", "No");
        return (
          <div key={d.key} className="flex items-center gap-3 py-1">
            <span className="text-xs uppercase tracking-wide text-slate-400 w-32 shrink-0">{d.label}</span>
            <span className="text-sm text-slate-700 break-words" data-testid={`cf-value-${d.key}`}>{v === undefined || v === null || v === "" ? "—" : String(v)}</span>
          </div>
        );
      })}
    </div>
  );
}

export function CustomFieldsEdit({ defs, values, onChange }) {
  if (!defs?.length) return null;
  const set = (k, v) => onChange({ ...(values || {}), [k]: v });
  return (
    <div className="space-y-3" data-testid="custom-fields-edit">
      {defs.map((d) => {
        const v = (values || {})[d.key] ?? "";
        return (
          <div key={d.key}>
            <Label className="flex items-center gap-1">{d.label}{d.required && <span className="text-red-500">*</span>}</Label>
            {d.type === "longtext" ? <Textarea data-testid={`cf-input-${d.key}`} value={v} onChange={(e) => set(d.key, e.target.value)} className="mt-1.5" />
              : d.type === "number" ? <Input data-testid={`cf-input-${d.key}`} type="number" value={v} onChange={(e) => set(d.key, e.target.value)} className="mt-1.5" />
              : d.type === "date" ? <Input data-testid={`cf-input-${d.key}`} type="date" value={v} onChange={(e) => set(d.key, e.target.value)} className="mt-1.5" />
              : d.type === "yesno" ? <label className="flex items-center gap-2 mt-1.5"><Checkbox data-testid={`cf-input-${d.key}`} checked={!!v} onCheckedChange={(c) => set(d.key, !!c)} /><span className="text-sm text-slate-600">{d.label}</span></label>
              : d.type === "select" ? (
                <Select value={v} onValueChange={(val) => set(d.key, val)}>
                  <SelectTrigger data-testid={`cf-input-${d.key}`} className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{(d.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : <Input data-testid={`cf-input-${d.key}`} value={v} onChange={(e) => set(d.key, e.target.value)} className="mt-1.5" />}
          </div>
        );
      })}
    </div>
  );
}

export function CustomFieldManager({ entity, open, onOpenChange, onChanged }) {
  const { t } = useI18n();
  const [defs, setDefs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [nf, setNf] = useState({ label: "", type: "text", required: false, options: "" });

  const load = () => client.get(`/custom-fields/${entity}`).then((r) => setDefs(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open, entity]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!nf.label.trim()) return;
    setBusy(true);
    try {
      await client.post(`/custom-fields/${entity}`, {
        label: nf.label, type: nf.type, required: nf.required, order: defs.length,
        options: nf.type === "select" ? nf.options.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      setNf({ label: "", type: "text", required: false, options: "" });
      load(); onChanged?.();
      toast.success(t("cf.saved", "Field added"));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!(await confirm({ title: t("cf.delTitle", "Delete field"), description: t("cf.delConfirm", "Delete this custom field definition?"), confirmText: t("common.delete", "Delete"), destructive: true }))) return;
    try { await client.delete(`/custom-fields/${entity}/${id}`); load(); onChanged?.(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="custom-field-manager" className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings2 className="h-5 w-5 text-brand" />{t("cf.manage", "Manage custom fields")}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-56 overflow-y-auto">
          {defs.length === 0 ? <p className="text-sm text-slate-400">{t("cf.none", "No custom fields yet.")}</p> :
            defs.map((d) => (
              <div key={d.id} data-testid={`cf-def-${d.id}`} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200">
                <span className="text-sm text-slate-700">{d.label} <span className="text-xs text-slate-400">· {d.type}{d.required ? " · required" : ""}</span></span>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" data-testid={`cf-del-${d.id}`} onClick={() => del(d.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
        </div>
        <div className="border-t border-slate-100 pt-3 space-y-3">
          <div><Label>{t("cf.label", "Field label")}</Label><Input data-testid="cf-new-label" value={nf.label} onChange={(e) => setNf({ ...nf, label: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("cf.type", "Type")}</Label>
              <Select value={nf.type} onValueChange={(v) => setNf({ ...nf, type: v })}>
                <SelectTrigger data-testid="cf-new-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t(`cf.type.${tp}`, tp)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 mt-6"><Checkbox data-testid="cf-new-required" checked={nf.required} onCheckedChange={(c) => setNf({ ...nf, required: !!c })} /><span className="text-sm">{t("cf.required", "Required")}</span></label>
          </div>
          {nf.type === "select" && <div><Label>{t("cf.options", "Options (comma separated)")}</Label><Input data-testid="cf-new-options" value={nf.options} onChange={(e) => setNf({ ...nf, options: e.target.value })} className="mt-1.5" /></div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("common.close", "Close")}</Button>
          <Button data-testid="cf-add-btn" onClick={add} disabled={busy || !nf.label.trim()} className="bg-brand hover:bg-brand-dark">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" />{t("common.add", "Add")}</>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
